<template>
  <v-container>
    <h1 class="text-center">Temporäre Sprachkanäle</h1>

    <h2>Einstellungen</h2>
    <v-row>
      <v-col cols="12" md="6">
        <div class="text-caption">Benutzergrenze</div>
        <v-slider
          v-model="userLimitSlider"
          min="0"
          max="100"
          thumb-label="always"
          hint="Standard-Benutzerlimit für alle temporären Sprachkanäle. Kann auf Discord direkt für jeden Kanal separat geändert werden."
          persistent-hint
        >
          <template v-slot:thumb-label="{ value }">{{ formatUserLimit(value) }}</template>
        </v-slider>
      </v-col>
      <v-col cols="12" md="6">
        <div class="text-caption">Bitrate (kbps)</div>
        <v-slider
          v-model="defaultBitrate"
          min="8"
          :max="maxBitrate"
          thumb-label="always"
          hint="Standard-Bitrate in kbps für alle temporären Sprachkanäle. Kann in Discord direkt für jeden Kanal separat geändert werden."
          persistent-hint
        ></v-slider>
      </v-col>
      <v-col cols="12" md="6">
        <div class="text-caption">Am Leben erhalten (Minuten)</div>
        <v-slider
          v-model="keepaliveSlider"
          min="0"
          max="61"
          thumb-label="always"
          hint='Dauer in Minuten, bis die temporären Kanäle gelöscht werden, nachdem alle den temporären Sprachkanal verlassen haben. 0 bedeutet sofort, ∞ bedeutet nie.'
          persistent-hint
        >
          <template v-slot:thumb-label="{ value }">{{ formatMinutes(value) }}</template>
        </v-slider>
      </v-col>
      <v-col cols="12" md="6">
        <div class="text-caption">Eigentumssperre (Minuten)</div>
        <v-slider
          v-model="ownershipLockSlider"
          min="0"
          max="61"
          thumb-label="always"
          hint='Dauer in Minuten, bis die temporären Kanäle für die Übernahme durch einen neuen Eigentümer verfügbar sind, nachdem der Eigentümer den temporären Sprachkanal verlassen hat. 0 bedeutet sofort, ∞ bedeutet nie.'
          persistent-hint
        >
          <template v-slot:thumb-label="{ value }">{{ formatMinutes(value) }}</template>
        </v-slider>
      </v-col>
    </v-row>

    <h2>Rollenberechtigungen</h2>
    <v-row>
      <v-col cols="12" md="4">
        <v-select
          :items="permissionModeOptions"
          item-text="text"
          item-value="value"
          label="Modus"
          v-model="permissionMode"
        ></v-select>
      </v-col>
      <v-col cols="12" md="7">
        <v-select
          :items="roles"
          item-text="name"
          item-value="id"
          label="Rollen"
          v-model="permissionRoleIds"
          multiple
          chips
          deletable-chips
        ></v-select>
      </v-col>
      <v-col cols="12" md="1" class="d-flex align-center">
        <v-btn @click="permissionRoleIds = roles.map((role) => role.id)" small outlined>Alle</v-btn>
      </v-col>
    </v-row>

    <h2>Ignorierte Rollen</h2>
    <p class="text-caption">Benutzer mit einer dieser Rollen werden NICHT von den /Voice-*-Befehlen betroffen sein.</p>
    <v-row>
      <v-col cols="12" md="11">
        <v-select
          :items="roles"
          item-text="name"
          item-value="id"
          label="Rollen"
          v-model="ignoredRoleIds"
          multiple
          chips
          deletable-chips
        ></v-select>
      </v-col>
      <v-col cols="12" md="1" class="d-flex align-center">
        <v-btn @click="ignoredRoleIds = roles.map((role) => role.id)" small outlined>Alle</v-btn>
      </v-col>
    </v-row>

    <h2>Moderator-Rollen</h2>
    <p class="text-caption">Benutzer mit einer dieser Rollen können die /Voice-*-Befehle ausführen, ohne Eigentümer der temporären Channels zu sein, und sind auch NICHT von ihnen betroffen.</p>
    <v-row>
      <v-col cols="12" md="11">
        <v-select
          :items="roles"
          item-text="name"
          item-value="id"
          label="Rollen"
          v-model="moderatorRoleIds"
          multiple
          chips
          deletable-chips
        ></v-select>
      </v-col>
      <v-col cols="12" md="1" class="d-flex align-center">
        <v-btn @click="moderatorRoleIds = roles.map((role) => role.id)" small outlined>Alle</v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-btn color="primary" :block="$vuetify.breakpoint.smAndDown" @click="saveValues">Speichern</v-btn>
      </v-col>
    </v-row>

    <h2>Hub-Kanäle (Join to Create)</h2>
    <v-row v-for="(hub, index) in hubs" :key="hub.id" align="center">
      <v-col cols="12" sm="4">
        <v-text-field v-model="hub.label" label="Name" outlined dense></v-text-field>
      </v-col>
      <v-col cols="12" sm="5">
        <v-text-field
          v-model="hub.name_template"
          label="Namens-Template"
          hint="Kann {index} und {username} enthalten."
          persistent-hint
          outlined
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="3" class="d-flex align-center">
        <v-btn @click="removeHub(index)" class="error" small block>Entfernen</v-btn>
      </v-col>
    </v-row>

    <v-row align="center">
      <v-col cols="12" sm="6">
        <v-select
          :items="categories"
          item-text="name"
          item-value="id"
          label="Kategorie für den neuen Hub-Kanal (optional)"
          clearable
          v-model="newHubCategoryId"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6">
        <v-btn
          @click="addHub"
          class="primary"
          :block="$vuetify.breakpoint.smAndDown"
          :loading="addingHub"
        >
          Hub-Kanal hinzufügen
        </v-btn>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="settingsSaved"
      timeout="3000"
      color="green"
    >
      Einstellungen gespeichert!
    </v-snackbar>
    <v-snackbar
      v-model="hubResult.show"
      timeout="5000"
      :color="hubResult.success ? 'green' : 'red'"
    >
      {{ hubResult.message }}
    </v-snackbar>
  </v-container>
</template>

<script>

import BackendService from '@/services/BackendService';

export default {
  data() {
    return {
      settingsSaved: false,
      addingHub: false,
      hubResult: {
        show: false,
        success: false,
        message: '',
      },
      defaultUserLimit: 5,
      defaultBitrate: 64,
      keepaliveMinutes: 0,
      ownershipLockMinutes: 0,
      maxBitrate: 96,
      hubs: [],
      categories: [],
      newHubCategoryId: null,
      roles: [],
      permissionMode: 'allow_except',
      permissionRoleIds: [],
      ignoredRoleIds: [],
      moderatorRoleIds: [],
      permissionModeOptions: [
        { text: 'Verweigern für alle Rollen außer', value: 'deny_except' },
        { text: 'Erlaubt sind alle Rollen außer', value: 'allow_except' },
      ],
    };
  },
  computed: {
    // Regler-Position 0-99 = direkter Wert, 100 = ∞ (auf Discord-Seite ohnehin gleichbedeutend mit "kein Limit")
    userLimitSlider: {
      get() {
        return this.defaultUserLimit === 0 ? 100 : this.defaultUserLimit;
      },
      set(value) {
        this.defaultUserLimit = value >= 100 ? 0 : value;
      },
    },
    // Regler-Position 0-60 = direkter Minutenwert, 61 = ∞ (gespeichert als -1)
    keepaliveSlider: {
      get() {
        return this.keepaliveMinutes === -1 ? 61 : this.keepaliveMinutes;
      },
      set(value) {
        this.keepaliveMinutes = value >= 61 ? -1 : value;
      },
    },
    ownershipLockSlider: {
      get() {
        return this.ownershipLockMinutes === -1 ? 61 : this.ownershipLockMinutes;
      },
      set(value) {
        this.ownershipLockMinutes = value >= 61 ? -1 : value;
      },
    },
  },
  mounted() {
    BackendService.loadVoiceSettings().then((data) => {
      this.defaultUserLimit = data.data.settings.default_user_limit;
      this.defaultBitrate = data.data.settings.default_bitrate;
      this.keepaliveMinutes = data.data.settings.keepalive_minutes;
      this.ownershipLockMinutes = data.data.settings.ownership_lock_minutes;
      this.hubs = data.data.hubs || [];
      this.categories = data.data.categories;
      this.maxBitrate = data.data.maxBitrate || 96;
      this.roles = data.data.roles || [];
      this.permissionMode = data.data.settings.permission_mode || 'allow_except';
      this.permissionRoleIds = data.data.permissionRoleIds || [];
      this.ignoredRoleIds = data.data.ignoredRoleIds || [];
      this.moderatorRoleIds = data.data.moderatorRoleIds || [];
    });
  },
  methods: {
    formatUserLimit(value) {
      return value >= 100 ? '∞' : String(value);
    },
    formatMinutes(value) {
      return value >= 61 ? '∞' : String(value);
    },
    addHub() {
      this.addingHub = true;
      BackendService.addVoiceHub({
        label: 'Join to Create',
        category_id: this.newHubCategoryId,
        name_template: '#{index} - {username}s Kanal',
      }).then((data) => {
        if (data.data.success) {
          this.hubs.push(data.data.hub);
          this.newHubCategoryId = null;
        } else {
          this.hubResult = { show: true, success: false, message: `Fehlgeschlagen: ${data.data.error}` };
        }
      }).catch(() => {
        this.hubResult = { show: true, success: false, message: 'Fehler bei der Verbindung zum Discord-Bot.' };
      }).finally(() => {
        this.addingHub = false;
      });
    },
    removeHub(index) {
      const hub = this.hubs[index];
      BackendService.deleteVoiceHub({ hub_id: hub.id }).then(() => {
        this.$delete(this.hubs, index);
      }).catch(() => {
        this.hubResult = { show: true, success: false, message: 'Fehler beim Löschen des Hub-Kanals.' };
      });
    },
    saveValues() {
      BackendService.saveVoiceSettings({
        default_user_limit: this.defaultUserLimit,
        default_bitrate: this.defaultBitrate,
        keepalive_minutes: this.keepaliveMinutes,
        ownership_lock_minutes: this.ownershipLockMinutes,
        permission_mode: this.permissionMode,
        permissionRoleIds: this.permissionRoleIds,
        ignoredRoleIds: this.ignoredRoleIds,
        moderatorRoleIds: this.moderatorRoleIds,
      }).then(() => {
        this.settingsSaved = true;
      }).catch(() => {
        this.hubResult = { show: true, success: false, message: 'Fehler beim Speichern der Einstellungen.' };
      });

      this.hubs.forEach((hub) => {
        BackendService.updateVoiceHub({
          hub_id: hub.id,
          label: hub.label,
          name_template: hub.name_template,
        });
      });
    },
  },
};
</script>
